import React from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import ImageBrowser from '../../shared/ImageBrowser';
import { useSetDefaultImage, useUserImages } from '../../../services/userImageService';
import { useVendorSubscriptionStatus } from '../../../services/subscriptionService';

const NON_PORTFOLIO_LIST_PARAMS = { non_portfolio: true };

const ImagesTab = () => {
 const { data: statusPayload, isLoading: subscriptionLoading, isError: subscriptionError } =
 useVendorSubscriptionStatus();

 const subscriptionReady = !subscriptionLoading && statusPayload?.success === true;
 const hasActiveSubscription = statusPayload?.data?.has_active_subscription === true;
 const currentSubscription = statusPayload?.data?.current_subscription;
 const maxGeneralImages = currentSubscription?.limits?.max_images;

 const { data: imagesResponse, isLoading: imagesLoading } = useUserImages(NON_PORTFOLIO_LIST_PARAMS);
 const galleryGeneralCount = imagesResponse?.data?.length ?? 0;
 const usageGeneralCount = currentSubscription?.usage?.images_count;
 const generalCount =
 subscriptionReady && typeof usageGeneralCount === 'number' ? usageGeneralCount : galleryGeneralCount;

 let maxImagesForBrowser = null;
 if (subscriptionReady) {
 if (!hasActiveSubscription) {
 maxImagesForBrowser = 0;
 } else if (maxGeneralImages != null) {
 maxImagesForBrowser = maxGeneralImages;
 } else {
 maxImagesForBrowser = null;
 }
 }

 const readyForLimitCopy = subscriptionReady && !imagesLoading;

 const remainingSlots =
 hasActiveSubscription && maxGeneralImages != null ? Math.max(0, maxGeneralImages - generalCount) : null;

 const atPlanLimit = hasActiveSubscription && maxGeneralImages != null && remainingSlots === 0;

 let statusLine = null;
 if (readyForLimitCopy) {
 if (!hasActiveSubscription) {
 statusLine = (
 <>
 Uploading general images requires an active subscription.{' '}
 <Link to="/vendor/subscription" className="text-ink font-medium underline hover:text-ink">
 View plans
 </Link>
 .
 </>
 );
 } else if (maxGeneralImages == null) {
 statusLine = `Your plan allows unlimited general images (portfolio not counted here). ${generalCount} uploaded.`;
 } else if (remainingSlots === 0) {
 statusLine = `You've reached your plan limit (${maxGeneralImages} general images). Delete one to add another or upgrade.`;
 } else {
 statusLine = `${remainingSlots} ${remainingSlots === 1 ? 'image' : 'images'} remaining (${generalCount}/${maxGeneralImages} general uploads used). Portfolio images use a separate limit.`;
 }
 }

 let guidelinesLimitLine = null;
 if (subscriptionError && !subscriptionLoading) {
 guidelinesLimitLine = <>Could not load your subscription. Refresh the page if limits look wrong.</>;
 } else if (!subscriptionReady) {
 guidelinesLimitLine = <>Checking your subscription for general image limits…</>;
 } else if (!hasActiveSubscription) {
 guidelinesLimitLine = <>Active subscription required — limits are set by your plan.</>;
 } else if (maxGeneralImages == null) {
 guidelinesLimitLine = <>Your plan includes unlimited general/profile uploads (excluding portfolio).</>;
 } else if (!imagesLoading) {
 guidelinesLimitLine = (
 <>
 Your plan allows up to {maxGeneralImages} non-portfolio images ({remainingSlots} slot
 {remainingSlots === 1 ? '' : 's'} left).
 </>
 );
 } else {
 guidelinesLimitLine = <>Your plan allows up to {maxGeneralImages} non-portfolio images.</>;
 }

 const setDefaultImageMutation = useSetDefaultImage();

 const handleImageSelect = (image) => {
 if (image) {
 setDefaultImageMutation.mutate(image.id);
 }
 };

 return (
 <div className="space-y-4 sm:space-y-6">
 <div>
 <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">General images</h3>
 <p className="text-sm text-gray-600">
 Upload images for your profile and gallery use. These are stored as <strong>general</strong> uploads and
 do not count toward your <strong>portfolio</strong> limit (see Portfolio tab). Your{' '}
 <strong>business logo</strong> is managed under Business Profile and stays out of this list.
 </p>
 {statusLine && (
 <p
 className={`text-sm font-medium mt-2 ${atPlanLimit || (!hasActiveSubscription && readyForLimitCopy) ? 'text-amber-700' : 'text-ink'}`}
 >
 {statusLine}
 </p>
 )}
 </div>

 <div className="bg-surface-muted rounded-lg p-4 sm:p-6 mb-4 sm:mb-6">
 <div className="flex items-start">
 <AlertCircle className="h-5 w-5 text-ink-tertiary mr-3 mt-0.5 flex-shrink-0" />
 <div className="min-w-0">
 <h4 className="text-sm font-medium text-ink">Image guidelines</h4>
 <ul className="text-sm text-ink mt-1 space-y-1 list-disc list-inside">
 <li>{guidelinesLimitLine}</li>
 <li>Supported formats: JPEG, PNG, GIF, WebP</li>
 <li>Maximum file size: 10MB</li>
 <li>Recommended dimensions: 1200x800px or larger</li>
 <li>Select an image below to mark it as your default for its type (used where your profile picks a gallery image)</li>
 </ul>
 </div>
 </div>
 </div>

 <ImageBrowser
 listQueryParams={NON_PORTFOLIO_LIST_PARAMS}
 uploadImageType="general"
 onSelectImage={handleImageSelect}
 showUpload={true}
 showDelete={true}
 showSetDefault={false}
 maxImages={maxImagesForBrowser}
 />
 </div>
 );
};

export default ImagesTab;
